/** iter 43 — Mobile / Proof view (one per Served attempt). */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import MobileShell from "../MobileShell";

const STATUS_COPY = {
  pending_render: { label: "Rendering…", color: "#f59e0b" },
  pending_notary: { label: "Awaiting notary", color: "#a855f7" },
  ready: { label: "Ready to send", color: "#16a34a" },
  sent: { label: "Sent", color: "#0ea5e9" },
  failed: { label: "Failed — tap re-render", color: "#dc2626" },
};

export default function MProof() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proof, setProof] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/proofs/${id}`);
      setProof(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  };
  useEffect(() => { load(); }, [id]);

  // Auto-poll while rendering.
  useEffect(() => {
    if (proof && proof.status === "pending_render") {
      const t = setTimeout(load, 1500);
      return () => clearTimeout(t);
    }
  });

  const reRender = async () => {
    setBusy(true);
    try { await api.post(`/proofs/${id}/render`); await load(); }
    finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/proofs/${id}/send`, {});
      setProof(r.data);
      if (r.data?.stubbed) {
        alert("Marked as sent (stubbed — add SENDGRID_API_KEY to enable real email).");
      }
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <MobileShell title="Proof of Service" back={proof?.job_id ? `/m/jobs/${proof.job_id}` : "/m/jobs"}>
      {err && <div className="m-card" style={{ background: "#fee2e2", color: "#991b1b" }}>{String(err)}</div>}
      {!err && !proof && <div className="m-empty">Loading…</div>}
      {proof && (
        <>
          <div className="m-card" data-testid="m-proof-summary">
            <div className="m-card-title">Status</div>
            <span
              className="m-chip"
              style={{
                background: STATUS_COPY[proof.status]?.color || "#999",
                color: "#fff",
              }}
              data-testid={`m-proof-status-${proof.status}`}
            >{STATUS_COPY[proof.status]?.label || proof.status}</span>
            {proof.notary_required && (
              <span className="m-chip" style={{ marginLeft: 6 }}>
                NOTARY {proof.notary_signed_at ? "✓ SIGNED" : "REQUIRED"}
              </span>
            )}
            {proof.error_text && (
              <div className="m-card-sub" style={{ color: "#991b1b", marginTop: 6 }}>
                {proof.error_text}
              </div>
            )}
          </div>

          {proof.pdf_url && (
            <div className="m-card">
              <div className="m-card-title">PDF preview</div>
              <iframe
                src={proof.pdf_url}
                title="Proof PDF"
                style={{ width: "100%", height: 420, border: "1px solid hsl(var(--border))", borderRadius: 8 }}
                data-testid="m-proof-pdf-iframe"
              />
              <a
                className="m-link"
                href={proof.pdf_url}
                target="_blank"
                rel="noreferrer"
                data-testid="m-proof-download"
              >Open in new tab ↗</a>
            </div>
          )}

          <div className="m-row">
            <button
              type="button"
              className="m-btn"
              onClick={reRender}
              disabled={busy}
              data-testid="m-proof-rerender"
            >RE-RENDER</button>
            <button
              type="button"
              className="m-btn m-btn-primary"
              onClick={send}
              disabled={busy || proof.status !== "ready"}
              data-testid="m-proof-send"
            >SEND</button>
          </div>

          {proof.sent_to_json && (
            <div className="m-card-sub" style={{ marginTop: 8 }}>
              Sent to: {(typeof proof.sent_to_json === "string"
                ? JSON.parse(proof.sent_to_json) : proof.sent_to_json).join(", ")}
            </div>
          )}
        </>
      )}
    </MobileShell>
  );
}
